import { useEffect, useRef, useState } from 'react';
import { Card, Col, Row, Button } from 'reactstrap';
import { InputLabel, Slider, TextField } from '@mui/material';
import { FaFilter, FaChevronDown, FaChevronUp, FaSave } from 'react-icons/fa';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import SelectAllSearch from '../Select/SelectAllSearch';
import moment from 'moment';
import PropTypes from 'prop-types';
import { selectStyles, colors } from '../../config/constants';
import useFiltersWidget, { PARENT_SIZE_MIN, PARENT_SIZE_MAX } from './useFiltersWidget';
import { useDispatch } from 'react-redux';
import { showModal } from '../../reducers/ModalsReducer/ModalsActions';
import FilterSetSaveModal from './FilterSetSaveModal';
import FilterSetUpdateModal from './FilterSetUpdateModal';
import LocalStorage from '../LocalStorage/LocalStorage';
import { setAppliedUsaIncluded } from '../../reducers/DashboardReducer/DashboardActions';
import SelectActionableOption from '../Select/SelectActionableOption';
import isEqual from 'lodash/isEqual';
import { isMobileOnly } from 'react-device-detect';

const sortFilterValues = (values = []) => [...values].sort((left, right) => `${left}`.localeCompare(`${right}`));

const normalizeFiltersForComparison = (filters = {}) => ({
    startDate: filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : null,
    endDate: filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : null,
    countries: sortFilterValues(filters.countries),
    states: sortFilterValues(filters.states),
    segments: sortFilterValues(filters.segments),
    distributors: sortFilterValues(filters.distributors),
    products: sortFilterValues(filters.products),
    children: sortFilterValues(filters.children),
    locations: sortFilterValues(filters.locations),
    categories: sortFilterValues(filters.categories),
    parentSizeMin: filters.parentSizeMin ?? null,
    parentSizeMax: filters.parentSizeMax ?? null,
    chainData: Boolean(filters.chainData),
    emergingData: Boolean(filters.emergingData)
});

const getSelectedDatasetOptions = (filters = {}) => {
    const datasets = [];

    if (filters.chainData) {
        datasets.push({ value: 'Chain Data', label: 'Chain Data' });
    }

    if (filters.emergingData) {
        datasets.push({ value: 'Emerging Chain Data', label: 'Emerging Chain Data' });
    }

    return datasets;
};

const FiltersWidget = (props) => {
    const dispatch = useDispatch();
    const datePickerRef = useRef();
    const currentUserId = LocalStorage.userData.get()?.user?.id;
    const isAdmin = LocalStorage.userData.get()?.user?.internalAdmin === true;
    const {
        selectedCategory, setSelectedCategory,
        selectedParentLocation, setSelectedParentLocation,
        selectedChildLocation, setSelectedChildLocation,
        selectedSegment, setSelectedSegments,
        selectedCountries, setSelectedCountries,
        selectedStates, setSelectedStates,
        selectedDatasets, setSelectedDatasets,
        selectedFilterSet, setSelectedFilterSet,
        selectedProduct, setSelectedProduct,
        selectedParentSize, setSelectedParentSize,
        getSelectedFilters,
        renameFilterSet, deleteFilterSet, setFilterSetAsDefault,
        allFilters, filterLoading,
        reportFilters, reportFiltersLoading,
        childrenFilters, childrenLoading,
        savedFilterSets, savedFilterSetsLoading,
        datasetOptions,
        fromDate, setFromDate,
        toDate, setToDate,
        setOriginalFromDate,
        setOriginalToDate,
        minDate, clearAllFilters,
        hasFiltersSelected,
        emergingData,
        chainData
    } = useFiltersWidget();
    const filterSetOptionsEnabled = props.filterSetOptionsEnabled ?? true;
    const datasetOptionsEnabled = (props.datasetOptionsEnabled && (emergingData && chainData)) ?? false;
    const saveFilterSetsEnabled = props.saveFilterSetsEnabled ?? true;
    const productOptionsEnabled = props.productOptionsEnabled ?? false;

    const selectedFilters = getSelectedFilters();
    const selectedFilterSetData = savedFilterSets?.find((filterSet) => filterSet.id === selectedFilterSet?.value) || null;
    const selectedFilterSetFilters = selectedFilterSetData?.filters || {};

    const prechosenFilters = props.prechosenFilters || {};
    const currentAppliedFilters = normalizeFiltersForComparison({ startDate: fromDate, endDate: toDate, ...selectedFilters });
    const propAppliedFilters = normalizeFiltersForComparison({
        startDate: props.fromDate ?? prechosenFilters.startDate,
        endDate: props.toDate ?? prechosenFilters.endDate,
        ...prechosenFilters
    });
    const filterSetHasChanges = !isEqual(
        normalizeFiltersForComparison(selectedFilterSetFilters),
        normalizeFiltersForComparison(selectedFilters)
    );

    useEffect(() => {
        if (Object.keys(prechosenFilters).length === 0) return;
        setSelectedCategory(prechosenFilters.categories?.map(c => ({value: c, label: c})) || []);
        setSelectedParentLocation(prechosenFilters.locations?.map(l => ({value: l, label: l})) || []);
        setSelectedChildLocation(prechosenFilters.children?.map(l => ({value: l, label: l})) || []);
        setSelectedProduct(prechosenFilters.products?.map(p => ({value: p, label: p})) || []);
        setSelectedSegments(prechosenFilters.segments?.map(s => ({value: s, label: s})) || []);
        setSelectedCountries(prechosenFilters.countries?.map(c => ({value: c, label: c})) || []);
        setSelectedStates(prechosenFilters.states?.map(s => ({value: s, label: s})) || []);
        setSelectedParentSize([
            prechosenFilters.parentSizeMin ?? PARENT_SIZE_MIN,
            prechosenFilters.parentSizeMax ?? PARENT_SIZE_MAX
        ]);
        setSelectedDatasets(getSelectedDatasetOptions(prechosenFilters));
        // eslint-disable-next-line
    }, [prechosenFilters]);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [previousSavedFilterSets, setPreviousSavedFilterSets] = useState(savedFilterSets);
    const [statesActive, setStatesActive] = useState(true);
    const [isDefaultFilterSet, setIsDefaultFilterSet] = useState(false);
    const [lastAppliedFilters, setLastAppliedFilters] = useState(propAppliedFilters);
    const filtersChangedSinceLastApply = !isEqual(currentAppliedFilters, lastAppliedFilters);
    const defaultSubtitle = 'Select from a variety of filters to narrow your search.';
    const saveDatasetsSubtitle = 'To save your filters as a new "Filter Set", simply click on the "Save as New Filter" option.';

    useEffect(() => {
        if (props.fromDate) {
            setFromDate(props.fromDate);
            setOriginalFromDate(props.fromDate);
        }
        if (props.toDate) {
            setToDate(props.toDate);
            setOriginalToDate(props.toDate);
        }
        if (props.minDate) {
            if (props.fromDate && moment(props.fromDate).isBefore(props.minDate)) {
                setFromDate(props.minDate);
                setOriginalFromDate(props.minDate);
            }
        }
        // eslint-disable-next-line
    }, [props.fromDate, props.toDate, props.minDate]);

    useEffect(() => {
        if (previousSavedFilterSets && savedFilterSets && previousSavedFilterSets.length < savedFilterSets.length) {
            const newFilterSet = savedFilterSets.find(set => !previousSavedFilterSets.some(prevSet => prevSet.id === set.id));
            if (newFilterSet) {
                setSelectedFilterSet({ value: newFilterSet.id, label: newFilterSet.name });
            }
            setPreviousSavedFilterSets(savedFilterSets);
        }
        if (savedFilterSets && savedFilterSets.length > 0 && !isDefaultFilterSet && !isAdmin) {
            const defaultSet = savedFilterSets.find((set) => set.isDefault && set.user_id === currentUserId);
            if (defaultSet) {
                setSelectedFilterSet({ value: defaultSet.id, label: defaultSet.name });
                setIsDefaultFilterSet(true);
            }
        }
        // eslint-disable-next-line
    }, [savedFilterSets]);

    useEffect(() => {
        props.onDateChange && props.onDateChange(fromDate, toDate);
        // eslint-disable-next-line
    }, [fromDate, toDate]);

    // Updates one end of the parent size range from a numeric input, clamping so the
    // bounds stay within [PARENT_SIZE_MIN, PARENT_SIZE_MAX] and never cross each other.
    const handleParentSizeInput = (bound, rawValue) => {
        const isMin = bound === 'min';
        const parsed = rawValue === '' ? (isMin ? PARENT_SIZE_MIN : PARENT_SIZE_MAX) : Math.trunc(Number(rawValue));
        if (Number.isNaN(parsed)) return;
        if (isMin) {
            setSelectedParentSize([Math.min(Math.max(parsed, PARENT_SIZE_MIN), selectedParentSize[1]), selectedParentSize[1]]);
        } else {
            setSelectedParentSize([selectedParentSize[0], Math.min(Math.max(parsed, selectedParentSize[0]), PARENT_SIZE_MAX)]);
        }
    };

    return (
        <div style={{width: '100%'}}>
            <Card className='px-3 py-2' style={{ textWrap: 'nowrap', background: 'white', backgroundImage: 'url("./FiltersBackground.png")', backgroundSize: 'cover', backgroundRepeat: 'no-repeat', zIndex: 2}}>
                <Col>
                    <Row>
                        <div className="main-header text-size-20" style={{color: colors.blueFilter}}><FaFilter fontSize={'15px'} style={{marginBottom: '5px'}}/> Filters</div>
                    </Row>
                    {!saveFilterSetsEnabled &&
                    <Row className='mb-1'>
                        <Col className="text-size-14" style={{fontWeight: '600', whiteSpace: 'break-spaces'}}>
                            {defaultSubtitle}
                        </Col>
                    </Row>
                    }
                    {saveFilterSetsEnabled &&
                    <Row className='mb-1'>
                        <Col className="text-size-14" style={{fontWeight: '600', whiteSpace: 'break-spaces'}}>
                            {saveDatasetsSubtitle}
                        </Col>
                    </Row>
                    }
                    <Row className='mb-2'>
                        {(savedFilterSets?.length > 0 && filterSetOptionsEnabled) &&
                        <Col style={{minWidth: '200px'}}>
                            <InputLabel style={{color: 'black', fontSize: '12px'}}>Saved Filter Sets</InputLabel>
                            <SelectActionableOption
                                placeholder='Select Filter Set'
                                isMulti={false}
                                onRenameConfirm={({id, newName}) => renameFilterSet(id, newName)}
                                onDeleteConfirm={({id}) => deleteFilterSet(id)}
                                onSetDefaultConfirm={({id}) => setFilterSetAsDefault(id)}
                                filterSetsData={savedFilterSets?.filter((filterSet) => filterSet.user_id === currentUserId) || []}
                                isLoading={savedFilterSetsLoading}
                                options={savedFilterSets?.filter((filterSet) => filterSet.user_id === currentUserId)?.map((filterSet) => ({value: filterSet.id, label: filterSet.name})) || []}
                                value={selectedFilterSet}
                                onChange={(filterSet) => setSelectedFilterSet(filterSet)}
                                isClearable={true}
                                id = {'saved-filter-sets-select'}
                            />
                        </Col>
                        }
                        <Col style={{minWidth: '200px'}}>
                            <InputLabel style={{color: 'black', fontSize: '12px'}}>Category</InputLabel>
                            <SelectAllSearch
                                placeholder='Select Category'
                                isMulti={true}
                                closeMenuOnSelect={false}
                                isLoading={filterLoading}
                                options={allFilters?.categories?.sort()?.map((category) => ({value: category, label: category})) || []}
                                value={selectedCategory}
                                onChange={(category) => setSelectedCategory(category)}
                            />
                        </Col>
                        <Col style={{minWidth: '200px'}}>
                            <InputLabel style={{color: 'black', fontSize: '12px'}}>Customer Segment</InputLabel>
                            <SelectAllSearch
                                placeholder='Select Segment'
                                isMulti={true}
                                closeMenuOnSelect={false}
                                isLoading={filterLoading}
                                options={allFilters?.segments?.sort()?.map((segment) => {
                                    const segmentLabel = segment.split('_').map(w => w[0].toUpperCase() + w.substring(1).toLowerCase()).join(' ');
                                    return {value: segment, label: segmentLabel};
                                }) || []}
                                value={selectedSegment}
                                onChange={(segment) => setSelectedSegments(segment)}
                            />
                        </Col>
                        <Col className='d-flex align-items-end' style={{minWidth: '150px'}}>
                            <Button
                                color='secondary'
                                size='sm'
                                onClick={() => setDrawerOpen(!drawerOpen)}
                                style={{paddingTop: '8px', paddingBottom: '8px', fontSize: '14px', color: '#49A4DA'}}
                                aria-label='Toggle More Filters'
                            >
                                <FaFilter /> {!drawerOpen ? 'More Filters' : 'Less Filters'} {drawerOpen ? <FaChevronUp style={{marginLeft: 6}}/> : <FaChevronDown style={{marginLeft: 6}}/>}
                            </Button>
                        </Col>
                    </Row>
                    <Row className='mt-3'>
                        {saveFilterSetsEnabled &&
                        <Col className={'d-flex gap-2' + (isMobileOnly ? ' justify-content-end pb-2' : ' justify-content-start'
                        )}>
                            {!selectedFilterSetData && hasFiltersSelected() && (
                            <Button
                                color='blueFilter'
                                size='sm'
                                disabled={!hasFiltersSelected()}
                                onClick={() => {dispatch(showModal(FilterSetSaveModal, { selectedFilters }));}}
                                style={{
                                    padding: '6px 24px',
                                    color: 'white'
                                }}
                            >
                                <FaSave style={{marginRight: '4px'}}/>Save as Filter Set
                            </Button>
                            )}
                            {selectedFilterSetData && filterSetHasChanges && (
                            <Button
                                color='blueFilter'
                                size='sm'
                                disabled={!filterSetHasChanges}
                                onClick={() => {
                                    dispatch(showModal(FilterSetUpdateModal, {
                                        filterSet: selectedFilterSet,
                                        existingFilters: selectedFilterSetFilters,
                                        existingFilterSetName: selectedFilterSetData?.name || selectedFilterSet?.label,
                                        selectedFilters
                                    }));
                                }}
                                style={{
                                    padding: '6px 24px',
                                    color: 'white'
                                }}
                            >
                                <FaSave style={{marginRight: '4px'}}/>Update Filter Set
                            </Button>
                            )}
                        </Col>
                        }
                        <Col className='d-flex justify-content-end gap-2'>
                            {hasFiltersSelected() && (
                                <Button
                                    outline
                                    color='mutedRed'
                                    size='sm'
                                    onClick={() => clearAllFilters(datePickerRef)}
                                    style={{
                                        padding: '6px 24px'
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            )}
                            <Button
                                color='blueFilter'
                                size='sm'
                                disabled={!filtersChangedSinceLastApply}
                                onClick={() => {
                                    if (props.onApplyFilters) {
                                        props.onApplyFilters({startDate: fromDate, endDate: toDate, ...selectedFilters});
                                        setLastAppliedFilters(currentAppliedFilters);
                                        if (selectedCountries && selectedCountries.length > 0) {
                                            if (selectedCountries?.some(c => c.value === 'US')) {
                                                dispatch(setAppliedUsaIncluded(true));
                                            } else {
                                                dispatch(setAppliedUsaIncluded(false));
                                            }
                                        }
                                        else if (allFilters?.countries?.some(c => c === "US")) {
                                            dispatch(setAppliedUsaIncluded(true));
                                        } else {
                                            dispatch(setAppliedUsaIncluded(false));
                                        }
                                    }
                                }}
                                style={{
                                    color: 'white',
                                    padding: '6px 24px'
                                }}
                            >
                                Apply Filters
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Card>
            <div
                style={{
                    width: '100%',
                    maxWidth: '100%',
                    maxHeight: drawerOpen ? 400 : 0,
                    background: 'white',
                    boxShadow: drawerOpen ? '0 1px 1px 0 rgba(0, 0, 0, 0.2)' : 'none',
                    borderRadius: '0 0 8px 8px',
                    padding: drawerOpen ? 24 : '0 24px',
                    position: 'relative',
                    zIndex: 1,
                    top: '-20px'
                }}
                aria-hidden={!drawerOpen}
            >
                {drawerOpen && (
                    <>
                        <Row className='mb-2'>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Date Range</InputLabel>
                                <DateRangePicker
                                    defaultStartDate={moment(fromDate).format('MM/DD/YYYY')}
                                    defaultEndDate={moment(toDate).format('MM/DD/YYYY')}
                                    minDate={moment(minDate).format('MM/DD/YYYY')}
                                    maxDate={moment().format('MM/DD/YYYY')}
                                    callback={(start, end) => {
                                        setFromDate(moment(start).format('YYYY-MM-DD'));
                                        setToDate(moment(end).format('YYYY-MM-DD'));
                                    }}
                                    ranges={props.dateRanges}
                                    childRef={datePickerRef}
                                />
                            </Col>
                            {productOptionsEnabled &&
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Product</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select Product'
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    isLoading={reportFiltersLoading}
                                    options={reportFilters?.products?.sort()?.map((product) => {
                                        const productLabel = product.productName
                                            .split(/[_ ]+/)
                                            .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                            .join(' ');
                                        return {value: product.productId, label: productLabel};
                                    }) || []}
                                    value={selectedProduct}
                                    onChange={(product) => setSelectedProduct(product)}
                                />
                            </Col>
                            }
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Parent Location</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select Parent Location'
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    isLoading={filterLoading}
                                    options={allFilters?.locations?.sort()?.map((location) => ({value: location, label: location})) || []}
                                    value={selectedParentLocation}
                                    onChange={(location) => setSelectedParentLocation(location)}
                                />
                            </Col>
                            <Col style={{minWidth: '260px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Parent Size (# of Locations)</InputLabel>
                                <div style={{padding: '18px 14px 0'}}>
                                    <Slider
                                        value={selectedParentSize}
                                        onChange={(event, newValue) => setSelectedParentSize(newValue)}
                                        valueLabelDisplay='auto'
                                        valueLabelFormat={(value) => value >= PARENT_SIZE_MAX ? `${PARENT_SIZE_MAX}+` : value}
                                        min={PARENT_SIZE_MIN}
                                        max={PARENT_SIZE_MAX}
                                        step={1}
                                        disableSwap
                                        aria-label='Parent size range'
                                        sx={{ color: colors.blueFilter }}
                                    />
                                    <div className='d-flex align-items-center gap-2 mt-1'>
                                        <TextField
                                            type='number'
                                            size='small'
                                            label='Min'
                                            value={selectedParentSize[0]}
                                            onChange={(e) => handleParentSizeInput('min', e.target.value)}
                                            inputProps={{ min: PARENT_SIZE_MIN, max: selectedParentSize[1], step: 1, 'aria-label': 'Minimum parent size' }}
                                            sx={{ flex: 1 }}
                                        />
                                        <span style={{ color: '#888' }}>–</span>
                                        <TextField
                                            type='number'
                                            size='small'
                                            label='Max'
                                            value={selectedParentSize[1]}
                                            onChange={(e) => handleParentSizeInput('max', e.target.value)}
                                            inputProps={{ min: selectedParentSize[0], max: PARENT_SIZE_MAX, step: 1, 'aria-label': 'Maximum parent size' }}
                                            sx={{ flex: 1 }}
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Child Location</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select Child Location'
                                    isMulti={true}
                                    isDisabled={!selectedParentLocation || selectedParentLocation.length === 0}
                                    closeMenuOnSelect={false}
                                    isLoading={childrenLoading || filterLoading}
                                    options={childrenFilters?.sort()?.map((child) => ({value: child, label: child})) || []}
                                    value={selectedChildLocation}
                                    onChange={(child) => setSelectedChildLocation(child)}
                                />
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Country</InputLabel>
                                <SelectAllSearch
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    name='country'
                                    styles={selectStyles}
                                    options = {allFilters?.countries?.sort()?.map((country) => ({value: country, label: country})) || []}
                                    onChange = {(e) => {
                                        setStatesActive((e && e.length > 0 && e.some(c => c.value !== 'US')) ? false : true);
                                        setSelectedCountries(e);
                                        if (!e.some(c => c.value === 'US')) {
                                            setSelectedStates([]);
                                        }
                                    }}
                                    value = {selectedCountries}
                                    placeholder='Select Country'
                                />
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>State/Territory</InputLabel>
                                <SelectAllSearch
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    name='state'
                                    styles={selectStyles}
                                    options = {allFilters?.states?.sort()?.map((state) => ({value: state, label: state})) || []}
                                    onChange = {(e) => setSelectedStates(e)}
                                    value = {selectedStates}
                                    placeholder='Select State/Territory'
                                    isDisabled={!statesActive}
                                />
                            </Col>
                            {datasetOptionsEnabled && (
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Datasets</InputLabel>
                                    <SelectAllSearch
                                        placeholder='Select Dataset'
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        options={datasetOptions}
                                        value={selectedDatasets}
                                        onChange={(dataset) => setSelectedDatasets(dataset)}
                                    />
                                </Col>
                            )}
                        </Row>
                    </>
                )}
            </div>
        </div>
    );
};

FiltersWidget.propTypes = {
    fromDate: PropTypes.instanceOf(Date),
    toDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    dateRanges: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.instanceOf(moment)
        )
    ),
    onDateChange: PropTypes.func,
    onApplyFilters: PropTypes.func,
    filterSetOptionsEnabled: PropTypes.bool,
    datasetOptionsEnabled: PropTypes.bool,
    productOptionsEnabled: PropTypes.bool,
    saveFilterSetsEnabled: PropTypes.bool,
    prechosenFilters: PropTypes.shape({
        states: PropTypes.arrayOf(PropTypes.string),
        segments: PropTypes.arrayOf(PropTypes.string),
        children: PropTypes.arrayOf(PropTypes.string),
        locations: PropTypes.arrayOf(PropTypes.string),
        categories: PropTypes.arrayOf(PropTypes.string),
        products: PropTypes.arrayOf(PropTypes.string),
        parentSizeMin: PropTypes.number,
        parentSizeMax: PropTypes.number,
        chainData: PropTypes.bool,
        emergingData: PropTypes.bool,
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date)
    })
};

export default FiltersWidget;
